<template>
    <div>
        <div v-if="section">
            <!-- Edit name modal -->
            <b-modal id="modal-edit-name"
                hide-header
                ok-title="Save"
                :ok-disabled="!section.name || !section.number || !section.category"
                @ok="onEditNameOK">
                <div class="form-group mb-3">
                    <label>Section Number <span class="text-danger">*</span></label>
                    <input type="text" class="form-control" v-model="section.number">
                </div>

                <div class="form-group mb-3">
                    <label>Category <span class="text-danger">*</span></label>
                    <input type="text" class="form-control" v-model="section.category">
                </div>

                <div class="form-group mb-3">
                    <label>Name <span class="text-danger">*</span></label>
                    <input type="text" class="form-control" v-model="section.name">
                </div>
            </b-modal>
            <!-- /Edit name modal -->

            <div class="card" v-if="viewState == 'Idle'">
                <div class="card-body">
                    <h4>
                        <span class="text-muted">Report Section Templates:&nbsp;</span> {{ sectionHeader }}
                        <button class="btn btn-light pl-1 pt-0 pb-0 pr-0" style="margin-top: -6px;" @click="$bvModal.show('modal-edit-name')">
                            <span class="mdi mdi-file-document-edit-outline text-primary" style="font-size: 1.5em;"></span>
                        </button>
                    </h4>
                    <router-link :to="{ name: 'report-section-template-index'}">
                        &laquo; Back to Section Templates
                    </router-link>
                    <hr>                

                    <div class="form-group mb-3">
                        <label>Default Content</label>
                        <div class="row">
                            <div class="col-6">
                                <b-form-select v-model="selectedReportField" :options="reportFieldOptions" size="sm" class="mb-1"></b-form-select>
                            </div>
                            <div class="col" style="margin-left: -20px;">
                                <button class="btn btn-sm mr-2" :class="selectedReportField ? 'btn-primary' : 'btn-light'" style="border: 1px solid #ccc;" :disabled="!selectedReportField" @click="insertReportField">
                                    <span class="mdi mdi-focus-field-horizontal" style="color: inherit;"></span> Insert Report Field
                                </button>
                                <button class="btn btn-light btn-sm" style="border: 1px solid #ccc;" @click="insertHeading">
                                    <span class="mdi mdi-format-text" style="color: inherit;"></span> Insert Heading
                                </button>
                            </div>
                        </div>
                        
                        <textarea class="form-control" rows="12" v-model="section.default_content" @blur="onContentAreaLostFocus" ref="contentArea"></textarea>
                    </div>

                    <!-- Selected report types -->
                    <div class="form-group mb-3">
                        <label>Selected Report Types</label>
                        <div class="mt-2">
                            <div class="report-type-selector mb-2" v-for="(reportType) in availableReportTypes" :key="reportType.code">
                                <label class="switch mr-1">
                                    <input type="checkbox" v-model="reportType.is_selected">
                                    <span class="slider round"></span>                            
                                </label>
                                <span style="cursor: pointer;" @click="reportType.is_selected = !reportType.is_selected">
                                    {{ reportType.name }} ({{ reportType.code }})
                                </span>
                            </div>
                        </div>
                    </div>
                    <!-- /Selected report types -->

                    <!-- Colour code -->
                    <div class="form-group">
                        <label>Colour Code</label>
                        <div class="input-group mb-3">
                            <div class="input-group-prepend">
                                <span class="input-group-text color-code-preview" :style="'background: ' + section.color_code"> </span>
                            </div>
                            <input type="text" class="form-control" style="max-width: 120px" v-model="section.color_code">
                        </div>
                    </div>
                    <!-- Colour code -->
                </div>
                <div class="card-footer">
                    <button class="btn btn-primary mr-2" @click="confirmUpdate" :disabled="!sectionIsValid || isUpdating || isDeleting">
                        <span class="mdi mdi-loading mdi-spin" v-if="isUpdating"></span>
                        <span class="mdi mdi-content-save-all-outline" v-if="!isUpdating"></span> Save Changes
                    </button>

                    <button class="btn btn-danger" @click="confirmDelete" :disabled="isDeleting || isUpdating">
                        <span class="mdi mdi-loading mdi-spin" v-if="isDeleting"></span>
                        <span class="mdi mdi-trash-can-outline" v-if="!isDeleting"></span> Delete
                    </button>
                </div>
            </div>
        </div>
        

        <!-- Loading info -->
        <div class="d-flex d-flex justify-content-center mt-5 mb-5" v-if="viewState == 'Busy'">
            <b-spinner variant="secondary" style="width: 1.8rem; height: 1.8rem;"></b-spinner>
        </div>
        <!-- /Loading info -->

        <div v-if="viewState == 'Error'">
            <div class="alert alert-danger">
                Something went wrong loading your data, please try again. 
                If the problem persists, please contact the support team.
            </div>
        </div>
    </div>
</template>

<script>
    import SectionTemplateService from '../../../services/templates/sectionTemplateService'
    import FieldTemplateService from '../../../services/templates/fieldTemplateService'
    import ReportTypeService from '../../../services/templates/reportTypeService'

    export default {
        props: {
            sectionId: String,
            isCreateResult: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                viewState: 'Idle',
                error: null,                
                section: null,
                sectionHeader: '',
                contentCursorPosStart: null,
                contentCursorPosEnd: null,
                reportFieldOptions: [],
                selectedReportField: null,
                availableReportTypes: [],
                isUpdating: false,
                isDeleting: false
            }
        },
        async mounted() {
            try {
                this.viewState = 'Busy'
                this.section = await SectionTemplateService.getReportSectionTemplateById(this.sectionId)                
                this.sectionHeader = `${this.section.number} ${this.section.category} - ${this.section.name}`

                if (this.section.default_content && this.section.default_content.indexOf('<br>' > -1)) {
                    let nlReplaced = this.section.default_content.replace(/<br>/g, '\n')
                    this.section.default_content = nlReplaced
                }

                // Get available report fields
                let reportFields = await FieldTemplateService.getReportFieldTemplates()
                let reportFieldSelectOpts = reportFields.map((f) => {
                    return {
                        value: `{{${f.name}}}`,
                        text: f.label
                    }
                })

                this.reportFieldOptions = [
                    { value: null, text: '--- Insert Report Field ---' },
                    ...reportFieldSelectOpts
                ]

                // Get available report types
                let reportTypes = await ReportTypeService.getReportTypes()
                this.availableReportTypes = reportTypes.map((t) => {
                    let isSelected = this.section.report_types.indexOf(t.code) > -1
                    return {
                        name: t.name,
                        code: t.code,
                        is_selected: isSelected
                    }
                })

                this.viewState = 'Idle'
                if (this.isCreateResult) {
                    this.$bvToast.toast(`Section template '${this.section.name}' created`, {
                        title: 'Success',
                        variant: 'success',
                        solid: true,
                        appendToast: true,
                        autoHideDelay: 3000
                    })
                }

            } catch (err) {                
                this.viewState = 'Error'
                this.error = err
            }
        },
        computed: {
            sectionIsValid() {
                return this.section.category
            }
        },
        methods: {
            onContentAreaLostFocus() {
                this.contentCursorPosStart = this.$refs.contentArea.selectionStart + 1
                this.contentCursorPosEnd = this.$refs.contentArea.selectionEnd + 1
            },
            insertHeading() {
                this.insertToContentAtCursor('\n\n**Heading**\n\n')
            },
            insertReportField() {
                this.insertToContentAtCursor(this.selectedReportField)
                this.selectedReportField = null
            },
            insertToContentAtCursor(val) {
                this.section.default_content = this.section.default_content || ''
                let baseStr = this.section.default_content
                let cursorPos = (this.contentCursorPosStart || this.section.default_content.length) - 1
                let insertedVal = `${cursorPos < 1 ? '' : ' '}${val}`
                this.section.default_content = [baseStr.slice(0, cursorPos), insertedVal, baseStr.slice(cursorPos)].join('')
            },
            confirmUpdate() {
                let msg = `Save changes to this section template? Please note, only UNEDITED reports will be updated`
                this.$bvModal
                    .msgBoxConfirm(msg, { centered: true })
                    .then(async (confirm) => {
                        if (confirm) {
                            await this.updateTemplate()
                        }
                    })
            },
            confirmDelete() {
                let msg = `Are you sure you want to delete this section template? This cannot be undone`
                this.$bvModal
                    .msgBoxConfirm(msg, { centered: true })
                    .then(async (confirm) => {
                        if (confirm) {
                            await this.deleteTemplate()
                        }
                    })
            },
            async onEditNameOK(evt) {
                evt.preventDefault()
                try {
                    await SectionTemplateService.updateReportSection(this.section.section_id, {
                        number: this.section.number,
                        category: this.section.category,
                        name: this.section.name
                    })

                    this.sectionHeader = `${this.section.number} ${this.section.category} - ${this.section.name}`
                    this.$bvModal.hide('modal-edit-name')
                    this.$bvToast.toast('Section template updated', {
                        title: 'Success',
                        variant: 'success',
                        solid: true,
                        appendToast: true,
                        autoHideDelay: 3000
                    })
                } catch (err) {
                    console.error(err)
                    this.$bvToast.toast('Sorry, something went wrong and we couldn\'t update the template', {
                        title: 'Error',
                        variant: 'danger',
                        solid: true,
                        appendToast: true,
                        autoHideDelay: 3000
                    })
                }
            },
            async updateTemplate() {                
                this.isUpdating = true
                try {
                    let selectedReportTypes = this.availableReportTypes
                        .filter((t) => { return t.is_selected })
                        .map((t) => { return t.code })

                    this.section.report_types = selectedReportTypes
                    if (this.section.color_code && !this.section.color_code.startsWith('#')) {
                        this.section.color_code = `#${this.section.color_code}`
                    }

                    await SectionTemplateService.updateReportSection(this.section.section_id, {
                        default_content: this.section.default_content,
                        report_types: this.section.report_types,
                        color_code: this.section.color_code ? this.section.color_code : '#FFF'
                    })

                    this.$bvToast.toast('Section template updated', {
                        title: 'Success',
                        variant: 'success',
                        solid: true,
                        appendToast: true,
                        autoHideDelay: 3000
                    })
                } catch (err) {
                    console.error(err)
                    this.$bvToast.toast('Sorry, something went wrong and we couldn\'t update the template', {
                        title: 'Error',
                        variant: 'danger',
                        solid: true,
                        appendToast: true,
                        autoHideDelay: 3000
                    })
                }

                this.isUpdating = false
            },
            async deleteTemplate() {
                this.isDeleting = true
                try {
                    await SectionTemplateService.deleteReportSection(this.section.section_id)
                    this.$router.push({
                        name: 'report-section-template-index'
                    })
                } catch (err) {
                    console.error(err)
                    this.$bvToast.toast('Sorry, something went wrong and we couldn\'t delete the template', {
                        title: 'Error',
                        variant: 'danger',
                        solid: true,
                        appendToast: true,
                        autoHideDelay: 3000
                    })
                }
            }
        }
    }
</script>

<style lang="scss">
    textarea {
        white-space: pre-wrap;
    }

    .report-type-selector {
        display: inline-block;
        margin-right: 1.3em;
    }

    .color-code-preview {
        width: 38px;
    }
</style>
